import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { DEFAULT_LABELS, type AppLabels } from '@/types'

interface TerminologyRow {
  term_collections: string | null
  term_recordings: string | null
  term_guests: string | null
  term_schedule: string | null
  term_appearances: string | null
}

/**
 * Hook to fetch the team's custom terminology settings.
 * Returns resolved labels (custom value or default).
 */
export function useTerminology() {
  const supabase = createClient()
  const { membership } = useAuth()

  return useQuery({
    queryKey: ['terminology', membership?.team_id],
    queryFn: async (): Promise<AppLabels> => {
      if (!membership?.team_id) return DEFAULT_LABELS

      const { data, error } = await supabase
        .from('production_settings')
        .select('term_collections, term_recordings, term_guests, term_schedule, term_appearances')
        .eq('team_id', membership.team_id)
        .maybeSingle()

      if (error) throw error

      if (!data) return DEFAULT_LABELS

      const row = data as TerminologyRow

      return {
        pipeline: DEFAULT_LABELS.pipeline, // Always "Flow"
        collections: row.term_collections || DEFAULT_LABELS.collections,
        recordings: row.term_recordings || DEFAULT_LABELS.recordings,
        guests: row.term_guests || DEFAULT_LABELS.guests,
        schedule: row.term_schedule || DEFAULT_LABELS.schedule,
        appearances: row.term_appearances || DEFAULT_LABELS.appearances,
      }
    },
    enabled: !!membership?.team_id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

interface UpdateTerminologyInput {
  term_collections?: string | null
  term_recordings?: string | null
  term_guests?: string | null
  term_schedule?: string | null
  term_appearances?: string | null
}

/**
 * Hook to update the team's custom terminology settings.
 */
export function useUpdateTerminology() {
  const supabase = createClient()
  const queryClient = useQueryClient()
  const { membership } = useAuth()

  return useMutation({
    mutationFn: async (input: UpdateTerminologyInput) => {
      if (!membership?.team_id) throw new Error('No team')

      const { data, error } = await supabase
        .from('production_settings')
        .update(input as never)
        .eq('team_id', membership.team_id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminology'] })
    },
  })
}
